import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  signal,
  ViewEncapsulation
} from '@angular/core';
import {Store} from '@ngrx/store';
import {KEYCLOAK_EVENT_SIGNAL, KeycloakEventType} from 'keycloak-angular';
import {AppActions} from 'src/app/app.action';
import {isMobileScreen} from '@shared/functions';
import { TranslateService } from '@ngx-translate/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import {FooterComponent} from '@core/components/footer/footer.component';
import {MenuComponent} from '@core/components/menu/containers/menu.component';
import {HeaderComponent} from '@core/components/header/header.component';
import {AsyncPipe, NgClass} from '@angular/common';
import {ToastModule} from 'primeng/toast';
import {MessageService} from 'primeng/api';
import {ButtonModule} from 'pu-core-library';
import {MesFinancementSearchFormActions} from '@features/mes-financements/store/search/search.actions';
import {DialogService} from 'primeng/dynamicdialog';
import {CategoryFolderEnum} from '@shared/enums/categorie.enum';
import {redirectUrl, urlsFooter} from '@shared/constants';
import {Actions, ofType} from '@ngrx/effects';
import {combineLatest, Observable, take} from 'rxjs';
import {AuthActions} from '@auth/store';
import {PageMaintenanceComponent} from 'src/app/pages/maintenance/maintenance.component';
import {MaintenanceActions} from '@features/maintenance/store/maintenance.actions';
import {maintenanceFeature} from '@features/maintenance/store/maintenance.reducers';
import {
  ModalCheckConditionUseComponent
} from '@shared/components/modal/modal-check-condition-use/modal-check-condition-use.component';
import {ModalNotificationsComponent} from '@shared/components/modal/modal-notifications/modal-notifications.component';
import Keycloak from 'keycloak-js';

@Component({
  standalone: true,
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        HeaderComponent,
        MenuComponent,
        NgClass,
        RouterOutlet,
        FooterComponent,
        ToastModule,
        ButtonModule,
        PageMaintenanceComponent,
        AsyncPipe
    ]
})
export class AppComponent implements AfterContentChecked {
  menuDisplay: boolean = !isMobileScreen();
  isLoggedIn = signal<boolean>(false);
  isHomePage!: boolean;
  loading = true;
  canAccessWithoutLogged: boolean = false;
  maintenance$ = this.store.select(maintenanceFeature.selectMaintenance);
  isCGGCPage!: boolean;
  private translationsReady$!: Observable<any>;
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  constructor(
    private store: Store,
    private changeDetector: ChangeDetectorRef,
    private translate: TranslateService,
    private router: Router,
    private messageService: MessageService,
    private actions$: Actions,
    private dialogService: DialogService,
    private keycloak : Keycloak
  ) {
    this.isLoggedIn.set(this.keycloak.authenticated || false);
    window.onresize = () => (this.menuDisplay = !isMobileScreen());
    this.isLoading();
    this.store.dispatch(
      MesFinancementSearchFormActions.updateCategorieDossier({
        categorieDossier: CategoryFolderEnum.BROUILLON,
      }),
    );
    effect(() => {
      const keycloakEvent = this.keycloakSignal();

      if (keycloakEvent.type === KeycloakEventType.TokenExpired) {
        this.keycloak.updateToken(20);
      }
      if (keycloakEvent.type === KeycloakEventType.Ready) {
        this.store.dispatch(MaintenanceActions.loadMaintenance());

        if (keycloakEvent.args === true || this.keycloak.authenticated) {
          this.initializeAuthenticatedSession();
        }
      }
      if (keycloakEvent.type === KeycloakEventType.AuthSuccess) {
        this.initializeAuthenticatedSession();
      }
    });
  }

  private initializeAuthenticatedSession(): void {
    this.isLoggedIn.set(true);
    this.initializeTranslations();
    this.store.dispatch(AppActions.init());

    this.listenForUseConditionsModal();
    this.listenForNotificationsModal();
  }

  private initializeTranslations(): void {
    // Initialiser les traductions en français.
    // À l'avenir, la langue pourra être stockée dans le local storage en fonction de la langue choisie par l'utilisateur.
    const defaultLang = 'fr';
    this.translate.setFallbackLang(defaultLang);
    this.translationsReady$ = this.translate.use(defaultLang);
  }

  listenForUseConditionsModal(): void {
    combineLatest([
      this.actions$.pipe(ofType(AuthActions.openUseConditionsModal), take(1)),
      this.translationsReady$
    ]).pipe(
      take(1)
    ).subscribe(([{ requiredDocs }]) => {
      this.dialogService.open(ModalCheckConditionUseComponent, {
        data: {
          labelConfirm: this.translate.instant('modal_condition_utilisation.accept'),
          labelCancel: this.translate.instant('modal_condition_utilisation.cancel'),
          description: this.translate.instant('modal_condition_utilisation.description'),
          requiredDocs
        },
        header: this.translate.instant('modal_condition_utilisation.header'),
        styleClass: 'custom-dialog-condition-use',
        style: { 'max-width': '800px' },
        closable: false,
        maskStyleClass: 'custom-backdrop-blur'
      });
    });
  }

  listenForNotificationsModal(): void {
    this.actions$.pipe(
      ofType(AuthActions.openNotificationsModal),
      take(1)
    ).subscribe(({ notifications }) => {
      this.dialogService.open(ModalNotificationsComponent, {
        data: {
          notifications
        },
        showHeader: false,
        styleClass: 'custom-dialog-notifications',
        style: { 'max-width': '800px' },
        closable: false,
        closeOnEscape: false,
        dismissableMask: false,
        maskStyleClass: 'custom-backdrop-blur'
      });
    });
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  createDispositif(subSubItem: any): void {
    const dispositifName = subSubItem.name.split('.')[0];

    if (dispositifName === 'menu_financement') {
      this.store.dispatch(
        AppActions.createOrResumeDispositifFinancement({ dispositif: subSubItem.dispositif }),
      );
    } else if (dispositifName === 'menu_paiement') {
      this.store.dispatch(
        AppActions.createOrResumeDispositifRemboursement({ dispositif: subSubItem.dispositif }),
      );
    } else {
      this.router.navigate(subSubItem.link);
    }
  }

  isLoading(): void {
    this.router.events.subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.loading = true;
          break;
        }
        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.loading = false;
          this.router.url === '/' || this.router.url === redirectUrl ? (this.isHomePage = true) : (this.isHomePage = false);
          this.canAccessWithoutLogged = urlsFooter.includes(this.router.url);
          this.isCGGCPage = this.router.url === '/cggc';
          break;
        }
        default: {
          break;
        }
      }
    });
  }

  redirect(url: string[]) {
    this.router.navigate(url, { queryParams: { redirectAllowed: true } });
    this.messageService.clear();
  }
  onClickOk(key?: string, event?: MouseEvent) {
    event?.stopPropagation();
    if (key) {
      this.messageService.clear(key);
    }
    this.messageService.clear();
  }
}
